/** Inventaire à la douchette (Mode Stock) : comptage et validation passent par le serveur. */
import { supabase } from '@/integrations/supabase/client';
import { appErrorFromSupabase } from '@/api/errors';
import type { AppError } from '@/api/errors';
import { err, ok } from '@/api/result';
import type { Result } from '@/api/result';
import { cleanScannedCode } from '@/lib/scanCode';

export type OpenInventory = {
  id: string;
  name: string;
  started_at: string;
  /** Premier inventaire : les articles non comptés passent à 0 à la validation. */
  start_from_zero: boolean;
};

/** Inventaire en cours, avec le nombre d'articles déjà comptés. */
export type OpenInventorySummary = OpenInventory & { counted_articles: number };

export type CountedItem = {
  id: string;
  product_id: string;
  counted_quantity: number;
  /** Écart avec le stock théorique au moment du comptage. */
  gap: number;
  name: string;
  counted_at: string | null;
};

export type StockModeProduct = {
  id: string;
  name: string;
  price: number;
  stock: number;
  barcode: string | null;
  image_url: string | null;
};

export type InventoryValidation = {
  already_validated: boolean;
  adjusted: number;
  unchanged: number;
  zeroed: number;
};

export const listOpenInventories = async (
  tenantId: string,
): Promise<Result<OpenInventorySummary[], AppError>> => {
  const { data, error } = await supabase
    .from('stock_inventories')
    .select('id, name, started_at, start_from_zero, stock_inventory_items(count)')
    .eq('tenant_id', tenantId)
    .eq('status', 'counting')
    .not('stock_inventory_items.counted_quantity', 'is', null)
    .order('started_at', { ascending: false });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.listOpenInventories'));
  return ok(
    (data ?? []).map(({ stock_inventory_items: counted, ...inventory }) => ({
      ...inventory,
      counted_articles: counted[0]?.count ?? 0,
    })),
  );
};

/** Validé ou abandonné depuis un autre écran : il ne faut plus y compter. */
export const isInventoryOpen = async (inventoryId: string): Promise<Result<boolean, AppError>> => {
  const { data, error } = await supabase
    .from('stock_inventories')
    .select('status')
    .eq('id', inventoryId)
    .maybeSingle();
  if (error) return err(appErrorFromSupabase(error, 'stockMode.isInventoryOpen'));
  return ok(data?.status === 'counting');
};

/** Le comptage est écarté, aucun stock ne change. */
export const abandonInventory = async (inventoryId: string): Promise<Result<void, AppError>> => {
  const { error } = await supabase
    .from('stock_inventories')
    .update({ status: 'cancelled' })
    .eq('id', inventoryId)
    .eq('status', 'counting');
  if (error) return err(appErrorFromSupabase(error, 'stockMode.abandonInventory'));
  return ok(undefined);
};

/** Sans liste figée d'articles : une ligne se crée au premier scan de chaque article. */
export const startCountInventory = async (
  tenantId: string,
  userId: string | null,
  name: string,
  startFromZero: boolean,
): Promise<Result<OpenInventory, AppError>> => {
  const { data, error } = await supabase
    .from('stock_inventories')
    .insert({
      tenant_id: tenantId,
      name,
      status: 'counting',
      created_by: userId,
      start_from_zero: startFromZero,
    })
    .select('id, name, started_at, start_from_zero')
    .single();
  if (error) return err(appErrorFromSupabase(error, 'stockMode.startCountInventory'));
  return ok(data);
};

export const listCountedItems = async (
  inventoryId: string,
): Promise<Result<CountedItem[], AppError>> => {
  const { data, error } = await supabase.rpc('inventory_counted_items', {
    p_inventory_id: inventoryId,
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.listCountedItems'));
  return ok(
    (data ?? []).map((row) => ({
      ...row,
      counted_quantity: Number(row.counted_quantity),
      gap: Number(row.gap),
    })),
  );
};

/** Article lu sur le serveur, jamais dans le cache du service worker. */
export const findStockModeProduct = async (
  tenantId: string,
  target: { code: string } | { productId: string },
): Promise<Result<StockModeProduct | null, AppError>> => {
  const { data, error } = await supabase.rpc('stock_mode_product', {
    p_tenant_id: tenantId,
    ...('code' in target ? { p_code: target.code } : { p_product_id: target.productId }),
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.findStockModeProduct'));
  const row = data?.[0];
  return ok(row ? { ...row, price: Number(row.price) } : null);
};

/** Liste de stock du PDA : recherche serveur par pages, jamais servie par le cache. */
export const searchStockProducts = async (
  tenantId: string,
  search: string,
  page: { limit: number; offset: number },
): Promise<Result<StockModeProduct[], AppError>> => {
  const { data, error } = await supabase.rpc('stock_mode_search', {
    p_tenant_id: tenantId,
    p_search: search,
    p_limit: page.limit,
    p_offset: page.offset,
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.searchStockProducts'));
  return ok((data ?? []).map((row) => ({ ...row, price: Number(row.price) })));
};

/**
 * Article d'un code scanné : code exact d'abord, puis code nettoyé (espaces, retours et
 * caractères de contrôle retirés) si rien n'est trouvé.
 */
export const findStockModeProductByScan = async (
  tenantId: string,
  rawCode: string,
): Promise<Result<StockModeProduct | null, AppError>> => {
  const exact = await findStockModeProduct(tenantId, { code: rawCode });
  if (!exact.ok || exact.value) return exact;
  const cleaned = cleanScannedCode(rawCode);
  if (!cleaned || cleaned === rawCode) return exact;
  return findStockModeProduct(tenantId, { code: cleaned });
};

export const setProductStock = async (
  productId: string,
  stock: number,
): Promise<Result<number, AppError>> => {
  const { data, error } = await supabase.rpc('set_product_stock', {
    p_product_id: productId,
    p_stock: stock,
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.setProductStock'));
  return ok(data);
};

export const countProduct = async (
  inventoryId: string,
  productId: string,
  change: { delta: number } | { value: number },
): Promise<Result<number, AppError>> => {
  const { data, error } = await supabase.rpc('inventory_count_product', {
    p_inventory_id: inventoryId,
    p_product_id: productId,
    ...('delta' in change ? { p_delta: change.delta } : { p_value: change.value }),
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.countProduct'));
  return ok(Number(data));
};

export const validateInventoryOnServer = async (
  inventoryId: string,
): Promise<Result<InventoryValidation, AppError>> => {
  const { data, error } = await supabase.rpc('validate_stock_inventory', {
    p_inventory_id: inventoryId,
  });
  if (error) return err(appErrorFromSupabase(error, 'stockMode.validateInventoryOnServer'));
  return ok(data as InventoryValidation);
};
